// sort-imports-ignore
// Must be imported before anything that uses zod
import "@/utils/zod-config"

import React from "react"
import { createRoot } from "react-dom/client"
import { HelmetProvider } from "react-helmet-async"

import { App } from "@/components/app/app"
import {
  AppVersionUpdateProvider,
  ConnectivityRecoveryProvider,
} from "@/contexts"
import type { EnvironmentName } from "@/models/environment.types"
// Needs to be bundled
import "@/utils/i18n"
import { queryClient } from "@/utils/queryClient"
import { datadogRum } from "@datadog/browser-rum"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import { StyledEngineProvider } from "@mui/material/styles"
import { LicenseInfo } from "@mui/x-license"

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY ?? "")

// Initialize RUM
if (
  process.env.REACT_APP_ENV === ("development" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("acceptance" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("fed" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("production" satisfies EnvironmentName)
) {
  datadogRum.init({
    actionNameAttribute: "data-analytics-action-name",
    allowedTracingUrls: [/https:\/\/.*\.(nzero|ledger8760)\.com/],
    applicationId: process.env.REACT_APP_DD_RUM_APPLICATION_ID ?? "",
    clientToken: process.env.REACT_APP_DD_RUM_CLIENT_TOKEN ?? "",
    defaultPrivacyLevel: "mask-user-input",
    env: process.env.REACT_APP_ENV,
    service: "puma",
    sessionReplaySampleRate:
      process.env.REACT_APP_ENV === ("production" satisfies EnvironmentName)
        ? 100
        : 0,
    sessionSampleRate: 100,
    site: "datadoghq.com",
    startSessionReplayRecordingManually: true,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: process.env.APP_VERSION,
  })
}

// Create root and render app
const rootElement = document.getElementById("root")

if (!rootElement) {
  throw new Error("Root element not found")
}

const root = createRoot(rootElement)

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <ConnectivityRecoveryProvider>
            <AppVersionUpdateProvider>
              <App />
            </AppVersionUpdateProvider>
          </ConnectivityRecoveryProvider>
        </StyledEngineProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
)
